import { Box, Button, TextField } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, TrashIcon } from '~/assets/icons';
import { BaseHeaderTitle } from '~/components/common';
import { useTranslation } from 'react-i18next';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { catchError, downloadCSV, handleApiError, notify } from '~/utils/helper';
import useDebounce from '~/hooks/useDebounce';

import styles from './Customer.module.scss';
import classNames from 'classnames/bind';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
// import { handleGetPriceItem } from '~/thunks/price-item/prgiceItemsThunk';
import { customerActions } from '~/thunks/customer/customerSlice';
import { handleGetCustomers, handleGetDetail } from '~/thunks/customer/customerThunk';
import CustomerList from '~/components/customer/CustomerList/CustomerList';
import customerApi from '~/apis/customer';
import { ICustomer, IPayloadDelete } from '~/utils/interface/customer';
import EditCustomerDialog from '~/components/customer/edit-customer-dialog/EditCustomerDialog';
import { CUSTOMER_TYPES_ORGANIZATION } from '~/utils/constants/customers';
import dayjs from 'dayjs';

const cx = classNames.bind(styles);

const Customer = () => {
  const permissions = usePermission();
  const [searchInput, setSearchInput] = useState<string>('');
  const { t } = useTranslation(['admin/customer', 'admin/component']);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editDialogMode, setEditDialogMode] = useState<string>('');

  const dispatch = useAppDispatch();
  const searchInputDebounce = useDebounce<string>(searchInput);
  const [editData, setEditData] = useState<ICustomer>();
  const { filter, needRefreshData, selectedIds, openAlertDelete, menuSelectedId } = useAppSelector(
    (state) => state.customer
  );

  const handleSetOpen = async (open: boolean, needRefreshData?: boolean) => {
    setOpenEditDialog(open);
    needRefreshData && dispatch(customerActions.changeNeedRefreshData(needRefreshData));
    if (!open) setEditData(undefined);
  };

  const handleOpen = () => {
    setEditDialogMode(DIALOG_MODE_CREATE);
    setOpenEditDialog(true);
  };
  const handleOpenEdit = async (id: string) => {
    const response = await dispatch(handleGetDetail(id)).unwrap();
    setEditData(response);
    setEditDialogMode(DIALOG_MODE_EDIT);
    setOpenEditDialog(true);
    try {
    } catch (e) {
      handleApiError(e);
    }
  };

  const openAlertDeleteModal = useCallback(() => {
    dispatch(customerActions.changeOpenAlertDelete(true));
  }, [dispatch]);

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(customerActions.changeMenuSelectId(undefined));
    dispatch(customerActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const onDeleteSelected = useCallback(async () => {
    let payloadDeleteCustomers: IPayloadDelete =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await customerApi.delete(payloadDeleteCustomers);
      notify('success', t('deleteSuccessMessage'));
      dispatch(customerActions.changeNeedRefreshData(true));
      dispatch(customerActions.changeOpenAlertDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, menuSelectedId, selectedIds, t]);

  useEffect(() => {
    dispatch(handleGetCustomers({ ...filter, type: 'organization' }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch, filter, needRefreshData]);

  useEffect(() => {
    if (dispatch && typeof searchInputDebounce !== 'undefined') {
      dispatch(
        customerActions.setFilter({
          search: searchInputDebounce,
          searchFields: ['name'],
          page: 1,
        })
      );
    }
  }, [searchInputDebounce, dispatch]);

  const handleExport = async () => {
    const now = dayjs().format('YYYY-MM-DD');
    const fileName = `Organization_Export_${now}.csv`;
    const res = await customerApi.export({ ...filter, type: 'organization' });
    downloadCSV(res.data, fileName);
  };
  return (
    <Box className='w-full h-full'>
      <Box className='mt-[20px] flex flex-col min-[1080px]:flex-row item-center'>
        <Box className='flex flex-col min-[1080px]:flex-row min-[1080px]:flex-1 min-[1080px]:justify-between'>
          <BaseHeaderTitle title={t('title')} />
          <Box className='flex flex-row mt-[10px] min-[1080px]:mt-0 gap-[10px]'>
            {permissions[PERMISSIONS.organization_delete] && (
              <Button
                className={cx(
                  { 'visibility: visible': selectedIds.length > 0 },
                  { 'visibility: hidden': selectedIds.length <= 0 }
                )}
                onClick={openAlertDeleteModal}
                variant='contained'
                color='error'
                disableElevation
                startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
              >
                {t('delete')}
              </Button>
            )}
            <TextField
              className='w-[204px]'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('searchPlaceholder')}
              InputProps={{ startAdornment: <SearchIcon className='w-[16px]' /> }}
            />
          </Box>
        </Box>

        {permissions[PERMISSIONS.organization_create] && (
          <Box className='flex flex-row mt-[10px] min-[1080px]:flex-row min-[1080px]:ml-[10px] min-[1080px]:mt-0 justify-between'>
            {permissions[PERMISSIONS.organization_view] && (
              <Button className={cx('export_container')} variant='contained' disableElevation onClick={handleExport}>
                {t('export')}
              </Button>
            )}
            <Button
              className='h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] ml-[10px]'
              variant='contained'
              disableElevation
              startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
              onClick={handleOpen}
            >
              {t('addNew')}
            </Button>
            <EditCustomerDialog
              open={openEditDialog}
              setOpen={handleSetOpen}
              mode={editDialogMode}
              data={editData}
              type={CUSTOMER_TYPES_ORGANIZATION}
            />
          </Box>
        )}
      </Box>
      <Box className='mt-[40px]'>
        <CustomerList onOpenEdit={handleOpenEdit} data={editData} />
      </Box>
      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescription')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelected}
      />
    </Box>
  );
};

export default Customer;
